import { isLegacyCustomCode } from '@superfunnel/types';
export { isLegacyCustomCode } from '@superfunnel/types';

/**
 * Helper utilities for managing custom codes in the dashboard
 */
/**
 * Generates a unique ID for a custom code
 */
const generateCustomCodeId = () => {
    return `cc_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};
/**
 * Creates a new custom code object with the new structure
 */
const createCustomCode = (name, createdBy, options) => {
    const now = new Date();
    return {
        id: generateCustomCodeId(),
        name,
        headCode: options.headCode,
        headDelay: options.headDelay,
        bodyCode: options.bodyCode,
        bodyDelay: options.bodyDelay,
        enabled: options.enabled ?? true,
        createdBy,
        createdAt: now,
        updatedAt: now,
    };
};
/**
 * Updates a custom code and sets the updatedAt timestamp
 */
const updateCustomCode = (customCode, updates) => {
    return {
        ...customCode,
        ...updates,
        updatedAt: new Date(),
    };
};
/**
 * Creates or updates a custom code reference
 */
const createCustomCodeReference = (id, enabled) => {
    return { id, enabled };
};
/**
 * Gets all custom codes that would be active at a given level
 * @param accountCodes - Custom codes from account level
 * @param dynamicPageCodes - Custom codes from dynamic page level
 * @param websiteCodes - Custom codes from website level
 * @param dynamicPageReferences - References at dynamic page level
 * @param websiteReferences - References at website level
 * @returns Array of custom codes with their final enabled state
 */
const getEffectiveCustomCodes = (accountCodes, dynamicPageCodes, websiteCodes, dynamicPageReferences, websiteReferences) => {
    const codesMap = new Map();
    // Add account codes
    accountCodes?.forEach((code) => {
        codesMap.set(code.id, { ...code });
    });
    // Add dynamic page codes (new ones only)
    dynamicPageCodes?.forEach((code) => {
        if (!codesMap.has(code.id)) {
            codesMap.set(code.id, { ...code });
        }
    });
    // Add website codes (new ones only)
    websiteCodes?.forEach((code) => {
        if (!codesMap.has(code.id)) {
            codesMap.set(code.id, { ...code });
        }
    });
    // Apply dynamic page references
    dynamicPageReferences?.forEach((ref) => {
        const code = codesMap.get(ref.id);
        if (code) {
            code.enabled = ref.enabled;
        }
    });
    // Apply website references
    websiteReferences?.forEach((ref) => {
        const code = codesMap.get(ref.id);
        if (code) {
            code.enabled = ref.enabled;
        }
    });
    return Array.from(codesMap.values());
};
/**
 * Gets the source level of a custom code
 */
const getCustomCodeSource = (codeId, accountCodes, dynamicPageCodes, websiteCodes) => {
    if (websiteCodes?.some((c) => c.id === codeId))
        return 'website';
    if (dynamicPageCodes?.some((c) => c.id === codeId))
        return 'dynamicPage';
    if (accountCodes?.some((c) => c.id === codeId))
        return 'account';
    return undefined;
};
/**
 * Checks if a custom code is overridden at a given level
 * A code is only considered overridden if a reference exists AND the enabled state differs from the original
 */
const isCustomCodeOverridden = (codeId, originalEnabled, references) => {
    const ref = references?.find((r) => r.id === codeId);
    if (!ref)
        return false;
    return ref.enabled !== originalEnabled;
};
/**
 * Gets the override state for a custom code at a given level
 */
const getCustomCodeOverride = (codeId, references) => {
    const ref = references?.find((r) => r.id === codeId);
    return ref?.enabled;
};
/**
 * Toggles a custom code at a given level by creating/updating a reference
 */
const toggleCustomCodeReference = (codeId, currentReferences, newEnabledState) => {
    const existingRefIndex = currentReferences.findIndex((ref) => ref.id === codeId);
    if (existingRefIndex >= 0) {
        // Update existing reference
        const updated = [...currentReferences];
        updated[existingRefIndex] = { id: codeId, enabled: newEnabledState };
        return updated;
    }
    else {
        // Add new reference
        return [...currentReferences, { id: codeId, enabled: newEnabledState }];
    }
};
/**
 * Removes a custom code reference
 */
const removeCustomCodeReference = (codeId, currentReferences) => {
    return currentReferences.filter((ref) => ref.id !== codeId);
};
/**
 * Validates a custom code (supports both new and legacy formats)
 */
const validateCustomCode = (code) => {
    const errors = [];
    if (!code.name || code.name.trim().length === 0) {
        errors.push('Name is required');
    }
    if (isLegacyCustomCode(code)) {
        // Legacy format validation
        if (!code.code || code.code.trim().length === 0) {
            errors.push('Code is required');
        }
        if (!code.location || !['head', 'body'].includes(code.location)) {
            errors.push('Location must be either "head" or "body"');
        }
        if (code.delay !== undefined && (code.delay < 0 || isNaN(code.delay))) {
            errors.push('Delay must be a non-negative number');
        }
    }
    else {
        // New format validation
        const hasHeadCode = code.headCode && code.headCode.trim().length > 0;
        const hasBodyCode = code.bodyCode && code.bodyCode.trim().length > 0;
        if (!hasHeadCode && !hasBodyCode) {
            errors.push('At least one of Head Code or Body Code is required');
        }
        if (code.headDelay !== undefined &&
            (code.headDelay < 0 || isNaN(code.headDelay))) {
            errors.push('Head delay must be a non-negative number');
        }
        if (code.bodyDelay !== undefined &&
            (code.bodyDelay < 0 || isNaN(code.bodyDelay))) {
            errors.push('Body delay must be a non-negative number');
        }
    }
    return {
        valid: errors.length === 0,
        errors,
    };
};
/**
 * Groups custom codes by location (supports both new and legacy formats)
 */
const groupCustomCodesByLocation = (codes) => {
    const head = [];
    const body = [];
    for (const code of codes) {
        if (isLegacyCustomCode(code)) {
            // Legacy format
            if (code.location === 'head') {
                head.push(code);
            }
            else {
                body.push(code);
            }
        }
        else {
            // New format - code can be in both
            if (code.headCode) {
                head.push(code);
            }
            if (code.bodyCode) {
                body.push(code);
            }
        }
    }
    return { head, body };
};
/**
 * Gets location display text for a custom code
 */
const getCustomCodeLocationDisplay = (customCode) => {
    if (isLegacyCustomCode(customCode)) {
        return customCode.location === 'head' ? 'Head' : 'Body';
    }
    const hasHead = !!customCode.headCode;
    const hasBody = !!customCode.bodyCode;
    if (hasHead && hasBody)
        return 'Head & Body';
    if (hasHead)
        return 'Head';
    if (hasBody)
        return 'Body';
    return 'None';
};
/**
 * Gets delay info display text for a custom code
 */
const getCustomCodeDelayDisplay = (customCode) => {
    if (isLegacyCustomCode(customCode)) {
        return customCode.delay && customCode.delay > 0
            ? `${customCode.delay}ms delay`
            : '';
    }
    const delays = [];
    if (customCode.headDelay && customCode.headDelay > 0) {
        delays.push(`Head: ${customCode.headDelay}ms`);
    }
    if (customCode.bodyDelay && customCode.bodyDelay > 0) {
        delays.push(`Body: ${customCode.bodyDelay}ms`);
    }
    return delays.join(', ');
};
/**
 * Sorts custom codes by creation date (newest first)
 */
const sortCustomCodesByDate = (codes) => {
    return [...codes].sort((a, b) => {
        const dateA = a.createdAt instanceof Date
            ? a.createdAt.getTime()
            : a.createdAt.seconds * 1000;
        const dateB = b.createdAt instanceof Date
            ? b.createdAt.getTime()
            : b.createdAt.seconds * 1000;
        return dateB - dateA;
    });
};

export { createCustomCode, createCustomCodeReference, generateCustomCodeId, getCustomCodeDelayDisplay, getCustomCodeLocationDisplay, getCustomCodeOverride, getCustomCodeSource, getEffectiveCustomCodes, groupCustomCodesByLocation, isCustomCodeOverridden, removeCustomCodeReference, sortCustomCodesByDate, toggleCustomCodeReference, updateCustomCode, validateCustomCode };
//# sourceMappingURL=custom-code.utils.esm.js.map
